import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitHandler, useForm } from "react-hook-form";

import {
    StreamingChannelInput,
    zStreamingChannelInput,
} from "@/components/forms/validation/streaming-channel";
import { ImageUploadInput } from "@/components/inputs/ImageInput";
import { Button } from "@/components/ui/button";
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    getHandleHelpText,
    getHandlePlaceholder,
    getPlatformIdHelpText,
    getPlatformIdPlaceholder,
    getProviderDisplayName,
    validateExternalChannelHandle,
    validateExternalChannelId,
} from "@/utils/streaming-channel";

export const StreamingChannelForm = ({
    onSubmit,
    defaultValues,
    isSubmitting = false,
}: {
    onSubmit: SubmitHandler<StreamingChannelInput>;
    defaultValues: StreamingChannelInput;
    isSubmitting?: boolean;
}) => {
    const form = useForm<StreamingChannelInput>({
        resolver: zodResolver(zStreamingChannelInput),
        defaultValues,
    });

    const watchedProvider = form.watch("provider");

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <FormField
                    control={form.control}
                    name="provider"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Provider</FormLabel>
                            <Select
                                onValueChange={field.onChange}
                                value={field.value}
                                defaultValue={defaultValues.provider}
                            >
                                <FormControl>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select streaming provider" />
                                    </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                    {(["YOUTUBE", "TWITCH", "KICK"] as const).map(provider => (
                                        <SelectItem key={provider} value={provider}>
                                            {getProviderDisplayName(provider)}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="externalChannelHandle"
                    rules={{
                        validate: watchedProvider
                            ? value =>
                                  validateExternalChannelHandle(value, watchedProvider) ||
                                  "Invalid handle format for this provider"
                            : undefined,
                    }}
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Channel Handle</FormLabel>
                            <FormControl>
                                <Input
                                    {...field}
                                    placeholder={
                                        watchedProvider
                                            ? getHandlePlaceholder(watchedProvider)
                                            : "Channel handle"
                                    }
                                />
                            </FormControl>
                            {watchedProvider && (
                                <FormDescription>
                                    {getHandleHelpText(watchedProvider)}
                                </FormDescription>
                            )}
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="externalChannelId"
                    rules={{
                        validate: watchedProvider
                            ? value =>
                                  validateExternalChannelId(value, watchedProvider) ||
                                  "Invalid channel ID format for this provider"
                            : undefined,
                    }}
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>External channel ID</FormLabel>
                            <FormControl>
                                <Input
                                    {...field}
                                    placeholder={
                                        watchedProvider
                                            ? getPlatformIdPlaceholder(watchedProvider)
                                            : "Platform-specific ID for channel"
                                    }
                                />
                            </FormControl>
                            {watchedProvider && (
                                <FormDescription>
                                    {getPlatformIdHelpText(watchedProvider)}
                                </FormDescription>
                            )}
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="imageId"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Channel Avatar</FormLabel>
                            <FormControl>
                                <ImageUploadInput
                                    imageId={field.value}
                                    onImageId={field.onChange}
                                />
                            </FormControl>
                            <FormDescription>
                                Optional avatar or thumbnail for the channel
                            </FormDescription>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <div className="flex justify-end">
                    <Button type="submit" disabled={isSubmitting}>
                        {isSubmitting ? "Saving..." : "Save Channel"}
                    </Button>
                </div>
            </form>
        </Form>
    );
};
